import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Star, Quote, ArrowRight, CheckCircle } from 'lucide-react';
import TestimonialCard from '../components/TestimonialCard';
import CTA from '../components/CTA';

const Testimonials: React.FC = () => {
  const testimonials = [
    {
      name: "Maria Schwab",
      location: "Belfast",
      content: `I’ve turned to Sharon after losing our long awaited and much loved baby in the 16th week of pregnancy. I was depressed and in a state of total functional freeze.

Sharon was recommended to me by my nutritionist and I have to admit that I was very sceptical initially, but with my mental health declining rapidly I decided to give it a go. Sharon is just wonderful - her warmth and kindness immediately put me at ease and made me feel like I was in good hands. I tried my best to follow Sharon’s lead, and already, the day after the first session I was able (actually felt like I could and wanted to do it) to start the day with 15 mins of exercise (yoga), do some journaling and practice gratitude. I know this doesn’t seem like much if you’re a healthy, well-functioning individual, but for someone who was barely surviving, consumed with grief and despair, this alone was a major breakthrough. The work we were doing in the weekly sessions meant that the walls around me started to slowly crumble and as a result I felt like I’ve had the capacity to make an effort to focus on my wellbeing and recovery, as well as being more focused on here and now, rather than the past or the future. I think I did 5 sessions in total, but I honestly could feel profound changes from the very first session. Sharon explained that we were going to be empowering the part of the brain that is responsible for executive functioning and suppressing the activity of the primitive brain that was preoccupied with sadness, worrying, ruminating and despair, but I honestly underestimated what a profound impact these sessions were going to have on me. In between the sessions I listened to audio recorded for me and sent to me by Sharon and this has become my favourite part of my bedtime routine. Along with grief, I was able to get under control my destructive habits, as well as shift my mindset to alleviate the depression, anxiety and the stress I’ve been dealing with over the past 6 months. I’ve started to experience joy again, and glimpses of real happiness and excitement for the future. I’m not sure where I would be without Sharon right now, but I can honestly say, no amount of group therapy or bereavement counselling could achieve so much progress in such a short space of time. Really highly recommend Sharon to anyone looking for help. She’s brought me back to life!`,
      rating: 5,
      service: "Grief & Trauma Recovery"
    },
    {
      name: "Nefeissa",
      location: "Northern Ireland",
      content: `Sharon is an incredible hypnotherapist and I would rate her 6 stars if the option was there .

Before trying out hypnotherapy, I was looking for a way to change my mindset and the deeply ingrained negative behavioural patterns that were influencing my life and making me unhappy. Hypnotherapy seems to have helped me get there .
Unlike many other therapy practices, hypnotherapy is not a talking therapy. It focuses more on changing habitual thinking patterns and setting up new more rational thinking patterns.
I had very little prior knowledge or experience of hypnotherapy and read about the practice quite by chance whilst sat in a hospital bed scrolling through my socail media.

Sharon is very professional with a gentle and understanding manner.
In the first session she explained to me how hypnotherapy works and gave a detailed explanation of the human mind and its functions . I found this deeply fascinating and essential to my understanding of the practice and my expectations.

I feel that Hypnotherapy and working with Sharon has helped me move forward in my life with better control over my anxiety and moods. I feel I am less reactive and more responsive to life challenges .
I am a happier person and less of a worrier .The change has been noticed by family and friends .
In addition to this, the physical ailments that have plagued me for several years have subsided.
How this has happened is hard to fully explain, however one thing is for sure, is that their is a strong connection between mind and body that cannot be undermined.`,
      rating: 5,
      service: "Medical Procedure Anxiety"
    },
    {
      name: "Jenny Barwell",
      location: "Belfast",
      content: `Excellent!
Sharon is a kind, thoughtful individual that listens to your needs. When I first tried hypnotherapy I wasn't sure that it would be of benefit for me. However, I went into it with an open mind and I was surprised how quickly I started to see results. Sharon was able to explain how it worked, and how your brain works, and I found this really interesting. I would definitely use hypnotherapy, and Sharon again in the future.`,
      rating: 5,
      service: "Health-Related Anxiety"
    },
    {
      name: "Stephanie Hughes",
      location: "Online",
      content: `I was nervous about having hypnotherapy but Sharon was very professional and trustworthy and she explained the process in such a way that really put me at ease. The hypnotherapy really helped with my stress levels and I loved the virtual consultations that I could work around my busy schedule.`,
      rating: 5,
      service: "Stress Management"
    },
    {
      name: "Jill",
      location: "Belfast",
      content: `Hypnotherapy was not at all what I expected it to be but so far it has worked.
Sharon was very professional, knowledgable, thoughtful and totally non-judgemental. She gave me the tools to move forward and help myself. I don't know how it worked, but it did.`,
      rating: 5,
      service: "Anxiety & Habits"
    }
  ];

  const stats = [
    { number: "100+", label: "Clients Helped" },
    { number: "CNHC", label: "Registered" },
    { number: "2+", label: "Years Experience" },
    { number: "AfSFH", label: "Member" }
  ];

  return (
    <>
      <Helmet>
        <title>Client Testimonials & Reviews | Sharon Clare Hypnotherapy Belfast & Lisburn</title>
        <meta 
          name="description" 
          content="Read real success stories from clients who transformed their lives with Sharon Clare's hypnotherapy services in Belfast, Lisburn & Northern Ireland. Anxiety relief, trauma recovery, and lasting change." 
        />
        <meta name="keywords" content="hypnotherapy reviews Belfast, hypnotherapy reviews Lisburn, hypnotherapy North Coast, hypnotherapy testimonials, client success stories, hypnotherapy before and after, Northern Ireland hypnotherapist reviews" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/testimonials/" />
        <meta property="og:title" content="Client Testimonials & Reviews | Sharon Clare Hypnotherapy Belfast & Lisburn" />
        <meta property="og:description" content="Read real success stories from clients who transformed their lives with Sharon Clare's hypnotherapy services." />
        <meta property="og:url" content="https://www.sharonclare-hypnotherapy.com/testimonials/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Client Testimonials & Reviews | Sharon Clare Hypnotherapy" />
        <meta name="twitter:description" content="Read real success stories from clients who transformed their lives with hypnotherapy." />
      </Helmet>
      
      <main>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Client Success Stories
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
              Real stories from real people who have transformed their lives with <a href="/services" className="text-accent-600 hover:text-accent-700 underline">clinical hypnotherapy</a>. 
              Discover how Sharon has helped clients at her Belfast and Lisburn clinics overcome <a href="/anxiety-stress" className="text-accent-600 hover:text-accent-700 underline">anxiety</a>, <a href="/radiotherapy-anxiety" className="text-accent-600 hover:text-accent-700 underline">radiotherapy fears</a>, and achieve their goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contact"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                Request a 10-Minute Call
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-accent-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              What My Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These testimonials represent just a few of the many success stories from clients 
              who have worked with me to overcome their challenges and achieve their goals.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...testimonials,
              {
                name: "Mark's Story",
                location: "Head & Neck Cancer Patient",
                content: `Mark began radiotherapy for head and neck cancer last week. On his first day, wearing the mask was overwhelming—it reminded him of a frightening childhood experience, and his body went straight into panic mode. Even with medication, stress balls, and calming music, he couldn't complete treatment. After hypnotherapy support, Mark was able to feel calmer, cope better, and complete treatment that once felt impossible.`,
                rating: 5,
                service: "Radiotherapy Anxiety Support"
              }
            ].map((testimonial, index) => (
              <TestimonialCard key={index} {...testimonial} />
            ))}
          </div>
        </div>
      </section>

      

      {/* Why Clients Choose Me */}
      <section className="py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Why Clients Choose Me
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The qualities that make the difference in your healing journey.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Compassionate Care",
                description: "Every client is treated with empathy, understanding, and respect for their unique journey and needs."
              },
              {
                title: "Proven Results",
                description: "Evidence-based techniques that have helped over 200 clients achieve lasting positive change."
              },
              {
                title: "Safe Environment",
                description: "A confidential, supportive space where you can feel comfortable and secure throughout your sessions."
              },
              {
                title: "Personalized Approach",
                description: "Every treatment plan is tailored to your specific goals, challenges, and comfort level."
              },
              {
                title: "Professional Excellence",
                description: "Continuous learning and professional development to provide you with the best possible care."
              },
              {
                title: "Ongoing Support",
                description: "Continued guidance and support between sessions to help you maintain your progress."
              }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-lg">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Trusted & Verified
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional qualifications and client verification ensure you receive the highest quality care.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              "Advanced Diploma in Clinical Hypnotherapy",
              "Certified Trauma-Informed Practitioner",
              "Member of National Council for Hypnotherapy",
              "2+ Years Clinical Experience"
            ].map((qualification, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center text-accent-600 mx-auto mb-4">
                  <CheckCircle className="w-8 h-8" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  {qualification}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Ready to Create Your Success Story?"
        subtitle="Join the hundreds of clients who have transformed their lives with hypnotherapy. Book your free 10-minute consultation today."
        primaryButtonText="Request a 10-Minute Call"
        primaryButtonHref="#contact"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
    </>
  );
};

export default Testimonials;
